import { always, tryCatch } from 'ramda';
import { History } from 'history';
import { ApolloClient } from '@apollo/client';
import { LocalState } from '@apollo/client/local-state';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { getGraphQLErrors } from '../../error';
import { hrefFor } from '../../../routers';
import { logger } from '../../../lib/logger';
import { isDevelopment } from '../../../lib/isEnv';
import { getCookies } from '../../../hooks/useCookies';
import { getUserFromCookies } from '../../../hooks/useUser';
import { createCache, initializeCache } from '../cache';
import { getSearchApiHeaders } from '../getSearchApiHeaders';
import { formatFeatureFlags } from '../formatFeatureFlags';
import { createCommonConfiguration, isUnauthorizedError } from './common';

const createClientErrorHandler =
  (history: History): ErrorLink.ErrorHandler =>
  ({ error, operation, result: response }) => {
    const graphQLErrors = getGraphQLErrors(error);
    const networkError = graphQLErrors ? undefined : error;
    if (isUnauthorizedError(error)) {
      if (response) response.errors = undefined;
      history.replace(hrefFor.LogoutPage());
    }

    const { id } = getUserFromCookies();

    logger.error(
      'APOLLO_CLIENT_ERROR',
      `Apollo: GraphQL Error during operation "${operation.operationName}"`,
      {
        graphQLErrors,
        networkError,
        operation,
        response,
        userId: `${id}`
      }
    );
  };

const flags = JSON.stringify(formatFeatureFlags(window.__FEATURE_FLAGS__ || {}));

export const createClientSideApolloClient = (history: History) => {
  const cache = createCache();
  const headersLink = new SetContextLink(({ headers }) => {
    const cookies = getCookies();
    const searchApiHeaders = getSearchApiHeaders(
      cookies,
      history.location.pathname + history.location.search
    );
    const { accessToken } = getUserFromCookies();

    return {
      headers: {
        ...headers,
        ...searchApiHeaders,
        authorization: accessToken ? `Bearer ${accessToken}` : ''
      }
    };
  });

  cache.restore(window.__APOLLO_STATE__);
  initializeCache(cache, {});

  const commonConfiguration = createCommonConfiguration({
    additionalRestHeaders: {
      'X-Mojo-Feature-Flags': flags
    }
  });

  return new ApolloClient({
    ...commonConfiguration,

    link: new ErrorLink(createClientErrorHandler(history))
      .concat(headersLink)
      .concat(commonConfiguration.link),

    cache,

    localState: new LocalState({}),

    devtools: {
      enabled: isDevelopment()
        ? true
        : tryCatch(devTools => JSON.parse(devTools), always(false))(window.__ENV__?.DEV_TOOLS)
    }
  });
};
